import { Environment, Environments, getEnvironment } from '../entities/environment';
import featureJson from '../feature-toggles.json';
import {
  DEFAULT_ENVIRONMENT,
  FEATURE_AB_TESTING,
  FEATURE_CHINESE_CDN_CONTENT_REQUEST,
  FEATURE_DEALER_SEARCH_CHINA,
  FEATURE_DEALER_SEARCH_OPENING_HOURS,
  FEATURE_FINDER_SHORTCUTS,
  FEATURE_NAVI_DRAWER_FROM_CDN,
  FEATURE_OVERRIDE_ENABLED,
  FEATURE_PCOM_SEARCH,
  FEATURE_SENTRY,
  FEATURE_SHOP,
  FEATURE_SHOP_WISHLIST
} from '../utility/constants';
import { getConfig, isString } from '../utility/helper';
import { NaviError, NaviErrorTypes } from '../utility/navi-error';
import logger from './logger';
import windowService from './window-service';
import { navStateStore } from '../state/nav-state-store';

type FeatureMap = { [featureName: string]: { enabled: boolean } };

export class FeatureToggles {
  features: FeatureMap = {};
  env: Environment = 'local';

  constructor(
    env = getEnvironment(DEFAULT_ENVIRONMENT),
    private readonly featureEnvMap: { [env: string]: FeatureMap } = featureJson
  ) {
    this.env = env;
    this.features = this.featureEnvMap[env];
  }

  private async loadRemoteToggles(): Promise<void> {
    try {
      const configs = getConfig(this.env);
      const locale = navStateStore.state.locale;
      let BFFEndpoint = configs.BFF_ENDPOINT;

      if (locale === 'en-CN' || locale === 'zh-CN') {
        BFFEndpoint = configs.BFF_CN_ENDPOINT;
      }

      const response = await fetch(`${BFFEndpoint}/toggles?env=${this.env}`);

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text);
      }

      const result = await response.json();

      this.features = result;
    } catch (err) {
      throw new NaviError(`Load Feature Toggles failed with error: ${err}`, NaviErrorTypes.GENERAL);
    }
  }

  private stringToBooleanStrict(value: string): boolean | null {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return null;
  }

  private getUrlFeatureToggleOverride(feature: string, searchString: string): boolean | null {
    const queryValue = searchString
      .split('?')[1]
      ?.split('&')
      .find((query) => query.includes(feature))
      ?.split('=')[1];

    return isString(queryValue) ? this.stringToBooleanStrict(queryValue || '') : null;
  }

  private getLocalStorageFeatureToggleOverride(feature: string): boolean | null {
    const localStorageValue = localStorage.getItem(`phn.${feature}`);

    return isString(localStorageValue) ? this.stringToBooleanStrict(localStorageValue) : null;
  }

  isFeatureEnabled(feature: string, searchString: string): boolean {
    if (this.features === undefined) {
      return false;
    }

    if (this.env === Environments.PRODUCTION && this.isFeatureNotReadyForProd(feature)) {
      return false;
    }

    if (this.features[FEATURE_OVERRIDE_ENABLED]?.enabled) {
      const featureToggleOverrides = [
        this.getUrlFeatureToggleOverride(feature, searchString),
        this.getLocalStorageFeatureToggleOverride(feature)
      ];

      for (const featureToggleOverride of featureToggleOverrides) {
        if (featureToggleOverride !== null) {
          return featureToggleOverride;
        }
      }
    }

    if (this.features[feature] !== undefined) {
      return this.features[feature].enabled;
    } else {
      return false;
    }
  }

  setEnvironment(env: Environment) {
    this.env = getEnvironment(env);
  }

  async setFeatures(env: Environment): Promise<void> {
    this.env = getEnvironment(env);
    try {
      if (env === 'test') {
        this.features = this.featureEnvMap[env];
        return;
      }
      await this.loadRemoteToggles();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logger.error('INIT_APP_CONFIG', error);
      this.features = this.featureEnvMap[env];
    }
  }

  isFeatureNotReadyForProd(feature: string): boolean {
    const prodDisabledToggles: string | string[] = [FEATURE_NAVI_DRAWER_FROM_CDN];
    return prodDisabledToggles.includes(feature);
  }

  isShopToggleActive = (): boolean => this.isFeatureEnabled(FEATURE_SHOP, windowService.location?.search || '');
  isPcomSearchToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_PCOM_SEARCH, windowService.location?.search || '');
  isABTestingToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_AB_TESTING, windowService.location?.search || '');
  isDealerSearchChinaEnabled = (): boolean =>
    this.isFeatureEnabled(FEATURE_DEALER_SEARCH_CHINA, windowService.location?.search || '');
  isDealerSearchOpeningHoursEnabled = (): boolean =>
    this.isFeatureEnabled(FEATURE_DEALER_SEARCH_OPENING_HOURS, windowService.location?.search || '');
  isSentryEnabled = (): boolean => this.isFeatureEnabled(FEATURE_SENTRY, windowService.location?.search || '');
  isShopWishlistToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_SHOP_WISHLIST, windowService.location?.search || '');
  isFinderShortcutsToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_FINDER_SHORTCUTS, windowService.location?.search || '');
  isChineseCdnContentRequestToggleActive = (): boolean =>
    this.isFeatureEnabled(FEATURE_CHINESE_CDN_CONTENT_REQUEST, windowService.location?.search || '');

  isNaviDrawerFromCDNEnabled = (): boolean =>
    this.isFeatureEnabled(FEATURE_NAVI_DRAWER_FROM_CDN, windowService.location?.search || '');
}

export default new FeatureToggles();
